// Types et constantes de la page publique des formulaires joints aux dossiers (route /formulaire-public/:jeton).
// Sous-ensemble de « adminvisastravelapp/src/formulaires/types.ts » (mêmes NOMS exportés, pour que visibilite.ts —
// copie du moteur — s'y branche sans changement) + contrats de l'API PUBLIQUE consommée par la page
// (« VisaTravelApp backend/formulairesPublics/routes.js », préfixe /formulaires-publics, plan §5.2).
// Module pur : ni axios, ni React.

// ---------------------------------------------------------------------------
// Définition d'un formulaire (snapshot formulaires_instances.definition, renvoyé par GET /:jeton)
// ---------------------------------------------------------------------------

export type TypeQuestion =
  | 'texte' | 'texte_long' | 'nombre' | 'date' | 'oui_non' | 'choix' | 'choix_multiple' | 'fichier' | 'info';

export type Operateur = 'egal' | 'different' | 'parmi' | 'pas_parmi' | 'contient' | 'contient_pas' | 'rempli' | 'vide';

export type FormatTexte = 'email' | 'telephone';

export type ExtensionFichier = 'pdf' | 'jpg' | 'jpeg' | 'png' | 'heic' | 'doc' | 'docx';

export interface Option {
  valeur: string;  // ^[a-z0-9_-]{1,40}$, unique dans la question
  libelle: string; // 1-300 caractères
}

// « Afficher seulement si <question> <op> <valeur> ». `valeur` : absente avec rempli/vide, tableau avec parmi/pas_parmi,
// chaîne sinon (toujours des chaînes, même pour une cible nombre : '1950').
export interface Condition {
  question: string;
  op: Operateur;
  valeur?: string | string[];
}

// Interface unique pour les 9 types : les attributs propres à un type sont optionnels et absents sur les autres types
// (la définition a été validée par le backend admin). `aide` et `obligatoire` sont toujours présents.
export interface Question {
  id: string;               // ^[a-z0-9_]{1,40}$, unique dans toute la définition
  type: TypeQuestion;
  libelle: string;          // 1-300
  aide: string;             // 0-500
  obligatoire: boolean;     // toujours false pour info
  condition?: Condition;
  options?: Option[];       // choix, choix_multiple : 1-50 options
  accept?: string[];        // fichier : sous-ensemble de EXTENSIONS_AUTORISEES (défaut : liste entière)
  max_fichiers?: number;    // fichier : 1..5 (défaut 1)
  min?: number;             // nombre
  max?: number;             // nombre ; texte (1..2000, défaut 2000) ; texte_long (1..5000, défaut 5000)
  format?: FormatTexte;     // texte uniquement
}

export interface Section {
  id: string;               // même regex et même espace d'unicité que les questions
  titre: string;            // 1-300
  condition?: Condition;    // ne vise qu'une question d'une section STRICTEMENT antérieure
  questions: Question[];    // au moins une
}

export interface Definition {
  schema: 1;
  intro: string;            // 0-2000, affichée avant la première étape
  message_fin: string;      // 0-2000, affichée après la soumission
  sections: Section[];      // 1-30
}

// ---------------------------------------------------------------------------
// Réponses (formulaires_instances.reponses, plan §3.3) — TOUJOURS réécrites par le backend client à chaque écriture :
// `visibles` / `sections_visibles` / `manquantes` sont calculés par moteur.js ; la page les recalcule localement
// (visibilite.ts, même moteur) pour réagir à la saisie avant l'enregistrement.
// ---------------------------------------------------------------------------

export interface FichierReponse {
  nom: string;          // nom réel sur disque : form-<question>-<ts>-<rand6>.<ext> (clé des routes /fichiers/:nom)
  nom_original: string; // nom choisi par le client (255 caractères max)
  taille: number;       // octets
  date: string;         // ISO
}

export type ValeurReponse = string | string[]; // string[] pour choix_multiple

export interface Reponses {
  valeurs: Record<string, ValeurReponse>;         // réponses des questions hors info / fichier (invisibles conservées jusqu'à la soumission)
  fichiers: Record<string, FichierReponse[]>;     // par question de type fichier
  visibles: string[];                             // ids des questions visibles (ordre du document)
  sections_visibles: string[];                    // ids des sections visibles
  manquantes: string[];                           // obligatoires visibles non remplies
  section_courante: string | null;                // dernière étape affichée (reprise « revenez avec le même lien »)
  calcule_le: string;                             // ISO
}

// ---------------------------------------------------------------------------
// API PUBLIQUE — préfixe ${API_BASE_URL}/formulaires-publics (formes exactes de routes.js, plan §5.2)
// ---------------------------------------------------------------------------

// Machine d'états (plan §3.4). Côté public : cree / envoye / en_cours = éditable ; soumis = récapitulatif (toute
// écriture → 409 deja_soumis) ; annule = 410 partout (jamais renvoyé par un 200).
export type StatutInstance = 'cree' | 'envoye' | 'en_cours' | 'soumis' | 'annule';

// 200 de GET /formulaires-publics/:jeton (Cache-Control: no-store ; jamais l'e-mail ni le chemin du dossier)
export interface FormulaireCharge {
  statut: StatutInstance;
  nom_modele: string;
  definition: Definition;
  reponses: Reponses | null;            // null tant que le client n'a rien enregistré
  version_reponses: number;             // verrou optimiste : à renvoyer tel quel à chaque écriture
  dossier: {
    prenom: string;                     // '' si inconnu
    nom: string;                        // '' si inconnu
    pays: string;                       // libellé du visa ('' si inconnu)
    id_demande: number;
  };
  date_soumission: string | null;       // ISO, renseignée une fois soumis
}

// 200 de PUT /:jeton/reponses, de DELETE /:jeton/fichiers/:nom et corps commun du 201 d'upload et du 422 de
// soumettre : l'état recalculé par le moteur après l'écriture. `version_reponses` est la version à réutiliser pour
// l'écriture suivante (un contenu identique répond 200 avec la même version, sans écriture).
export interface EtatEcriture {
  version_reponses: number;
  visibles: string[];
  sections_visibles: string[];
  manquantes: string[];
}

// 201 de POST /:jeton/fichiers/:question (multipart, seul champ « fichier »)
export type ReponseUpload = EtatEcriture & { fichier: FichierReponse };

// 200 de POST /:jeton/soumettre : `reponses` purgées (valeurs et fichiers des questions invisibles retirés) pour
// afficher le récapitulatif sans re-GET.
export interface ReponseSoumission {
  statut: 'soumis';
  date_soumission: string;              // ISO
  version_reponses: number;
  reponses: Reponses;
}

// Une erreur de validation à la soumission (422) : une seule par question, message français du moteur
export interface ErreurSoumission {
  question: string;
  message: string;
}

// Erreur normalisée rendue par toutes les fonctions de formulairesPublicsServices (jamais l'erreur axios brute).
//  - status : code HTTP, ou null si le serveur n'a pas répondu (réseau, délai, backend arrêté) ;
//  - code : 'version' (409 : conflit de version, avec `etat`), 'deja_soumis' (409) — absent sinon ;
//  - message : celui du corps JSON, sinon un message français par défaut selon le status ;
//  - etat : sur un 409 version, l'état en base { reponses, version_reponses, statut } à adopter ;
//  - erreurs : sur un 422 de soumettre, les questions en erreur ;
//  - ecriture : sur un 422 de soumettre, le brouillon a été ENREGISTRÉ : { version_reponses, visibles,
//    sections_visibles, manquantes } à adopter (sinon la prochaine écriture répond 409 version).
export interface ErreurPublique {
  status: number | null;
  code?: string;
  message: string;
  etat?: { reponses: Reponses | null; version_reponses: number; statut: StatutInstance };
  erreurs?: ErreurSoumission[];
  ecriture?: EtatEcriture;
}

// Limites d'upload du backend client (routes.js : TAILLE_MAX_FICHIER, MAX_FICHIERS_INSTANCE) — contrôlées côté
// client avant tout appel (413 / 409 sinon)
export const LIMITES_FICHIER = {
  tailleMax: 20 * 1024 * 1024, // 20 Mo par fichier
  parInstance: 30,             // fichiers au total sur un formulaire
};

// ---------------------------------------------------------------------------
// Constantes (copies EXACTES de schema.js / moteur.js, mêmes noms que côté admin)
// ---------------------------------------------------------------------------

export const TYPES: TypeQuestion[] = ['texte', 'texte_long', 'nombre', 'date', 'oui_non', 'choix', 'choix_multiple', 'fichier', 'info'];
export const OPERATEURS: Operateur[] = ['egal', 'different', 'parmi', 'pas_parmi', 'contient', 'contient_pas', 'rempli', 'vide'];

// Opérateurs autorisés selon le type de la question CIBLE d'une condition. `info` n'est jamais référençable.
export const OPERATEURS_PAR_TYPE: Record<TypeQuestion, Operateur[]> = {
  choix: ['egal', 'different', 'parmi', 'pas_parmi', 'rempli', 'vide'],
  oui_non: ['egal', 'different', 'parmi', 'pas_parmi', 'rempli', 'vide'],
  texte: ['egal', 'different', 'contient', 'contient_pas', 'rempli', 'vide'],
  texte_long: ['egal', 'different', 'contient', 'contient_pas', 'rempli', 'vide'],
  nombre: ['egal', 'different', 'parmi', 'pas_parmi', 'rempli', 'vide'],
  date: ['egal', 'different', 'rempli', 'vide'],
  choix_multiple: ['contient', 'contient_pas', 'parmi', 'pas_parmi', 'rempli', 'vide'],
  fichier: ['rempli', 'vide'],
  info: [],
};

// Opérateurs dont la `valeur` est un tableau / absente
export const OPERATEURS_LISTE: Operateur[] = ['parmi', 'pas_parmi'];
export const OPERATEURS_SANS_VALEUR: Operateur[] = ['rempli', 'vide'];

// Liste blanche des extensions d'une question fichier (défaut : la liste entière) ; le backend refuse le reste (400)
export const EXTENSIONS_AUTORISEES: ExtensionFichier[] = ['pdf', 'jpg', 'jpeg', 'png', 'heic', 'doc', 'docx'];

export const REGEX = {
  id: /^[a-z0-9_]{1,40}$/,            // id de section ou de question
  valeurOption: /^[a-z0-9_-]{1,40}$/, // valeur d'une option
  numerique: /^-?\d+(\.\d+)?$/,       // `valeur` de condition sur une cible nombre
  dateIso: /^\d{4}-\d{2}-\d{2}$/,     // date stockée aaaa-mm-jj
};

// ---------------------------------------------------------------------------
// Libellés (fr)
// ---------------------------------------------------------------------------

export const LIBELLES_TYPES: Record<TypeQuestion, string> = {
  texte: 'Texte court',
  texte_long: 'Texte long',
  nombre: 'Nombre',
  date: 'Date',
  oui_non: 'Oui / Non',
  choix: 'Choix unique',
  choix_multiple: 'Choix multiple',
  fichier: 'Fichier à joindre',
  info: "Paragraphe d'information",
};

// Options fixes d'une question oui_non (rendu RadioGroup et valeur lisible)
export const OPTIONS_OUI_NON: Option[] = [
  { valeur: 'oui', libelle: 'Oui' },
  { valeur: 'non', libelle: 'Non' },
];

// Options d'une question selon son type : options (choix, choix_multiple), Oui/Non (oui_non), null sinon
// (saisie libre). Sert au rendu et aux valeurs lisibles (libelles.ts).
export const optionsCible = (cible: Question): Option[] | null => {
  if (cible.type === 'choix' || cible.type === 'choix_multiple') return cible.options || [];
  if (cible.type === 'oui_non') return OPTIONS_OUI_NON;
  return null;
};
